/**
 * Ficha de una observación (2026-10-07): qué se encontró, en qué está, la
 * evidencia de cada levantamiento y el historial (quién y cuándo). Según los
 * permisos de quien la abre: levantar, aprobar o rechazar (con motivo).
 */
import {
  rec_AccionAsync as AccionAsync,
  rec_EstadoError as EstadoError,
  rec_ImagenActa as ImagenActa,
  rec_Modal as Modal,
  rec_React as React,
  rec_ResolverObservacion as ResolverObservacion,
  rec_api as api,
} from "../../runtime/index.jsx";
import "./observaciones.css";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useState = (...a) => React.useState(...a);

export const ESTADOS_OBSERVACION = {
  PENDIENTE: { texto: `Por levantar`, clase: `ob-pendiente` },
  POR_APROBAR: { texto: `Por aprobar`, clase: `ob-por-aprobar` },
  APROBADA: { texto: `Aprobada`, clase: `ob-aprobada` },
  RESUELTA: { texto: `Resuelta`, clase: `ob-aprobada` },
  RECHAZADA: { texto: `Rechazada`, clase: `ob-rechazada` },
};

const ACCIONES = {
  CREADA: `Se encontró en el acta`,
  LEVANTADA: `Levantada`,
  APROBADA: `Aprobada`,
  RECHAZADA: `Rechazada`,
  ARRASTRADA: `Vuelve en otra acta`,
  RESUELTA: `Resuelta`,
  AVISADA: `Aviso al cerrar el acta`,
  RECORDADA: `Recordatorio: vence hoy`,
  ESCALADA: `Vencida: se avisó al jefe`,
};

/** Horas en texto corto: 5 h, 1 d 4 h. */
const duracion = (h) => {
  if (h == null) return null;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `${Math.round(h)} h`;
  const d = Math.floor(h / 24), r = Math.round(h - d * 24);
  return r ? `${d} d ${r} h` : `${d} d`;
};

/** Trazabilidad de tiempos: plazo, aviso, levantamiento y cierre. */
function TiemposObservacion({ t, compromiso }) {
  if (!t) return null;
  const plazo = t.aTiempo === true ? { texto: `Levantada a tiempo`, clase: `ob-aprobada` }
    : t.aTiempo === false ? { texto: `Levantada con ${t.diasDeAtraso} ${t.diasDeAtraso === 1 ? `día` : `días`} de atraso`, clase: `ob-rechazada` }
    : t.diasDeAtraso ? { texto: `Vencida hace ${t.diasDeAtraso} ${t.diasDeAtraso === 1 ? `día` : `días`}`, clase: `ob-rechazada` }
    : { texto: `Dentro del plazo`, clase: `ob-pendiente` };
  return (
    <div className="ob-tiempos">
      <span className={`ob-estado ${plazo.clase}`}>{plazo.texto}</span>
      <dl className="ob-datos">
        <dt>Plazo</dt><dd>{dia(compromiso)}{t.plazoPorCriticidad ? ` · según criticidad` : ` · lo puso el inspector`}</dd>
        <dt>Encontrada</dt><dd>{fecha(t.encontradaEn)}</dd>
        <dt>Avisada</dt><dd>{t.avisadaEn ? fecha(t.avisadaEn) : `—`}</dd>
        {t.recordadaEn && <><dt>Recordatorio</dt><dd>{fecha(t.recordadaEn)}</dd></>}
        {t.escaladaEn && <><dt>Escalada al jefe</dt><dd>{fecha(t.escaladaEn)}</dd></>}
        <dt>Levantada</dt><dd>{t.primerLevantamientoEn ? `${fecha(t.primerLevantamientoEn)}${t.horasHastaLevantar != null ? ` · ${duracion(t.horasHastaLevantar)} desde el aviso` : ``}` : `—`}</dd>
        {t.cerradaEn && <><dt>Cerrada</dt><dd>{fecha(t.cerradaEn)}{t.horasHastaCerrar != null ? ` · ${duracion(t.horasHastaCerrar)} en total` : ``}</dd></>}
      </dl>
    </div>
  );
}

const fecha = (v) => (v ? new Date(v).toLocaleString(`es-PE`, { day: `2-digit`, month: `short`, hour: `2-digit`, minute: `2-digit` }) : `—`);
const dia = (v) => (v ? new Date(`${v}T12:00:00`).toLocaleDateString(`es-PE`, { day: `2-digit`, month: `short`, year: `numeric` }) : `—`);

export function EstadoObservacion({ estado }) {
  const e = ESTADOS_OBSERVACION[estado] ?? { texto: estado, clase: `` };
  return <span className={`ob-estado ${e.clase}`}>{e.texto}</span>;
}

export function FichaObservacion({ id, onCerrar, onCambio }) {
  const [f, setF] = useState(null);
  const [error, setError] = useState(``);
  const [levantando, setLevantando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState(``);

  const cargar = () => api(`/inspecciones/observaciones/${id}`, { sinCache: true }).then(setF).catch((e) => setError(e.message));
  useEffect(() => { cargar(); }, [id]);

  const decidir = async (accion) => {
    setError(``);
    try {
      await api(`/inspecciones/observaciones/${id}/${accion}`, {
        method: `POST`,
        ...(accion === `rechazar` ? { body: JSON.stringify({ motivo }) } : {}),
      });
      setRechazando(false);
      setMotivo(``);
      await cargar();
      onCambio?.();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Modal titulo={f?.etiqueta ?? `Observación`} onClose={onCerrar}>
      {!f ? (
        error ? <EstadoError mensaje={error} /> : <p className="text-sm text-(--text)">Cargando…</p>
      ) : (
        <div className="ob-ficha">
          <div className="ob-cabecera">
            <EstadoObservacion estado={f.estado} />
            {f.criticidad && <span className="ob-chip">{f.criticidad}</span>}
            {f.concesionariaNombre && <span className="ob-chip">🏢 {f.concesionariaNombre}</span>}
            {f.levantamientos > 1 && <span className="ob-chip">{f.levantamientos}.º levantamiento</span>}
          </div>
          <dl className="ob-datos">
            <dt>Acta</dt><dd>{f.acta} · {f.unidadNombre}</dd>
            <dt>Observación</dt><dd>{f.observacion}</dd>
            <dt>Acción correctiva</dt><dd>{f.accion}</dd>
            <dt>Responsable</dt><dd>{f.responsable}</dd>
            {f.origenObservacionId && <><dt>Viene de</dt><dd>Una observación rechazada en un acta anterior</dd></>}
            {f.solucion && <><dt>Cómo se levantó</dt><dd>{f.solucion}</dd></>}
            {f.motivoRechazo && <><dt>Motivo del rechazo</dt><dd className="ob-rechazo">{f.motivoRechazo}</dd></>}
          </dl>

          <TiemposObservacion t={f.tiempos} compromiso={f.fechaCompromiso} />

          {f.evidencias.length > 0 && (
            <div className="ob-evidencias">
              {f.evidencias.map((e) => <ImagenActa key={e.archivoId} actaId={f.inspeccionId} archivo={{ id: e.archivoId, mime: e.mime, campo: `foto:levantamiento` }} alto="h-28" />)}
            </div>
          )}

          {f.estado === `RECHAZADA` && (
            <p className="ob-aviso">
              {f.arrastradaAInspeccionId ? `Ya volvió en el acta #${f.arrastradaAInspeccionId}.` : `Vuelve en la próxima inspección de esta concesionaria.`}
            </p>
          )}

          <ol className="ob-historial">
            {f.historial.map((h, i) => (
              <li key={i}>
                <span className="ob-cuando">{fecha(h.en)}</span>
                <span><strong>{ACCIONES[h.accion] ?? h.accion}</strong>{h.actorNombre ? ` · ${h.actorNombre}` : ``}</span>
                {h.detalle && <span className="ob-detalle">{h.detalle}</span>}
              </li>
            ))}
          </ol>

          <EstadoError mensaje={error} />

          {levantando ? (
            <ResolverObservacion observacion={{ id: f.id, etiqueta: f.etiqueta }} onCancelar={() => setLevantando(false)}
              onListo={async () => { setLevantando(false); await cargar(); onCambio?.(); }} />
          ) : rechazando ? (
            <div className="flex flex-col gap-2">
              <textarea className="ob-motivo" rows={3} maxLength={1000} autoFocus placeholder="¿Por qué se rechaza? La concesionaria lo verá y la observación vuelve en la próxima inspección."
                value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              <div className="flex justify-end gap-2">
                <AccionAsync variante="secundario" onClick={() => setRechazando(false)}>Cancelar</AccionAsync>
                <AccionAsync variante="peligro" disabled={!motivo.trim()} onClick={() => decidir(`rechazar`)}>Rechazar</AccionAsync>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              {f.puedeLevantar && <AccionAsync onClick={() => setLevantando(true)}>Levantar</AccionAsync>}
              {f.puedeRechazar && <AccionAsync variante="secundario" onClick={() => setRechazando(true)}>Rechazar</AccionAsync>}
              {f.puedeAprobar && <AccionAsync onClick={() => decidir(`aprobar`)}>Aprobar</AccionAsync>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
